/** A width and a height, in pixels. */
export interface Size {
  width: number;
  height: number;
}

/** A pixel of the terminal's framebuffer. */
export interface FramebufferPoint {
  x: number;
  y: number;
}

/** A position from the top-left corner of a box, in CSS pixels. */
export interface BoxOffset {
  left: number;
  top: number;
}

/** The position noVNC's gesture handler gives each gesture event on its canvas. */
interface GesturePoint {
  clientX: number;
  clientY: number;
}

/** A pointer position as the page measured it, and the framebuffer pixel it falls on. */
interface MappedPointer extends FramebufferPoint {
  clientX: number;
  clientY: number;
}

/** Browser APIs the rotation runs on; tests replace them. */
export interface ScreenRotationDeps {
  /** Calls `onResize` whenever `element` changes size, until the returned function is called. */
  observeResize(element: Element, onResize: () => void): () => void;
}

const BROWSER_DEPS: ScreenRotationDeps = {
  observeResize: (element, onResize) => {
    const observer = new ResizeObserver(() => onResize());
    observer.observe(element);
    return () => observer.disconnect();
  },
};

/** The mouse events noVNC reads a position from, all dispatched on its canvas. */
const POINTER_EVENTS = ['mousedown', 'mouseup', 'mousemove', 'wheel'];
/** The events noVNC's gesture handler dispatches on its canvas for touch input. */
const GESTURE_EVENTS = ['gesturestart', 'gesturemove', 'gestureend'];
/** Listeners on the screen element see each event before noVNC's own, on the canvas. */
const CAPTURE = true;

const CENTRED = '50%';
const AUTO = 'auto';
const CENTRING = 'translate(-50%, -50%)';
const QUARTER_TURN_CLOCKWISE = 'rotate(90deg)';
/** The style properties the rotation sets on the screen element, and clears when it stops. */
const TURN_STYLE_PROPERTIES = [
  'left',
  'top',
  'right',
  'bottom',
  'width',
  'height',
  'transform',
];

/**
 * Where gesture positions are moved to: off the page, so that the cursor noVNC draws at a gesture's
 * position on touch devices hides instead of showing in the wrong place.
 */
const GESTURE_FRAME_ORIGIN = -100_000;
const NO_POINTER: MappedPointer = { clientX: 0, clientY: 0, x: 0, y: 0 };

/**
 * The framebuffer pixel under a position in the turned view (agronave-app
 * `remotesupport/PROTOCOL.md`, Screen orientation): for a W×H framebuffer the turned view is H wide
 * and W tall, and its pixel (u, v) is the framebuffer's x = v, y = H − 1 − u. `view` is the size the
 * turned view takes on the page; positions outside it fall on its nearest edge.
 */
export function framebufferPointOf(
  offset: BoxOffset,
  view: Size,
  framebuffer: Size,
): FramebufferPoint {
  const u = Math.floor((offset.left * framebuffer.height) / view.width);
  const v = Math.floor((offset.top * framebuffer.width) / view.height);
  return {
    x: clamp(v, framebuffer.width - 1),
    y: clamp(framebuffer.height - 1 - u, framebuffer.height - 1),
  };
}

/** The largest scale at which a framebuffer, turned a quarter, fits in `room`. */
export function turnedScale(room: Size, framebuffer: Size): number {
  return Math.min(
    room.width / framebuffer.height,
    room.height / framebuffer.width,
  );
}

function clamp(value: number, max: number): number {
  return Math.min(Math.max(value, 0), max);
}

/** A DOMRect for noVNC to measure the canvas by, without needing the DOMRect constructor. */
function rectOf(left: number, top: number, size: Size): DOMRect {
  return {
    x: left,
    y: top,
    left,
    top,
    width: size.width,
    height: size.height,
    right: left + size.width,
    bottom: top + size.height,
    toJSON() {
      return this;
    },
  };
}

/**
 * Shows the terminal's screen upright when its app draws it turned 90° counter-clockwise
 * (agronave-app `remotesupport/PROTOCOL.md`, Screen orientation), by turning the element noVNC
 * draws in a quarter clockwise. The element's parent is the room the turned screen fits in.
 *
 * @remarks
 * noVNC cannot turn its view, and a CSS rotation alone breaks it: it reads each pointer position
 * against the canvas's bounding box, which a rotation turns too. So, while the screen is turned:
 * - noVNC draws at the framebuffer's own size (`scaleViewport` off, which RemoteSupportSession sets
 *   for a rotated screen), and this class scales the element as it turns it;
 * - each mouse and gesture event is read on its way down to the canvas (capture phase), before
 *   noVNC reads it, and mapped to the framebuffer pixel the protocol names;
 * - the canvas's `getBoundingClientRect` is replaced by a frame placed so that noVNC's own arithmetic
 *   (`clientToElement`, then a scale of 1) lands that position on that pixel.
 *
 * Mouse events keep their positions, so the cursor noVNC draws on touch devices follows the pointer.
 * Gesture positions are rewritten into one frame instead, because noVNC places a double tap on the
 * first tap's position, measured while the second tap is handled.
 */
export class ScreenRotation {
  private rotated = false;
  /** The canvas whose bounding box is replaced; set only while the screen is turned. */
  private canvas: HTMLCanvasElement | null = null;
  private framebuffer: Size = { width: 0, height: 0 };
  private pointer: MappedPointer | null = null;
  private readonly stopObserving: () => void = () => undefined;
  /** Watches the turned canvas, whose size changes when the terminal sends another resolution. */
  private stopObservingCanvas: (() => void) | null = null;
  private readonly onPointer = (event: Event) =>
    this.mapPointer(event as MouseEvent);
  private readonly onGesture = (event: Event) =>
    this.mapGesture(event as CustomEvent<GesturePoint>);

  constructor(
    private readonly screen: HTMLElement,
    private readonly deps: ScreenRotationDeps = BROWSER_DEPS,
  ) {
    POINTER_EVENTS.forEach((type) =>
      screen.addEventListener(type, this.onPointer, CAPTURE),
    );
    GESTURE_EVENTS.forEach((type) =>
      screen.addEventListener(type, this.onGesture, CAPTURE),
    );
    const room = screen.parentElement;
    if (room) {
      this.stopObserving = deps.observeResize(room, () => this.layout());
    }
  }

  /**
   * Turns the screen when `rotated`, and shows it as it comes otherwise. Call it again whenever
   * noVNC shows a new screen: the turn needs the framebuffer's size, which noVNC knows once
   * connected.
   */
  update(rotated: boolean): void {
    this.rotated = rotated;
    this.layout();
  }

  dispose(): void {
    POINTER_EVENTS.forEach((type) =>
      this.screen.removeEventListener(type, this.onPointer, CAPTURE),
    );
    GESTURE_EVENTS.forEach((type) =>
      this.screen.removeEventListener(type, this.onGesture, CAPTURE),
    );
    this.stopObserving();
    this.straighten();
  }

  private layout(): void {
    const canvas = this.screen.querySelector('canvas');
    const room = this.screen.parentElement;
    if (
      this.rotated &&
      canvas &&
      room &&
      canvas.width > 0 &&
      canvas.height > 0
    ) {
      this.turn(canvas, room);
    } else {
      this.straighten();
    }
  }

  /** At a scale of 1 the canvas's size in pixels is the framebuffer's. */
  private turn(canvas: HTMLCanvasElement, room: HTMLElement): void {
    const framebuffer = { width: canvas.width, height: canvas.height };
    const scale = turnedScale(
      { width: room.clientWidth, height: room.clientHeight },
      framebuffer,
    );
    const style = this.screen.style;
    style.left = CENTRED;
    style.top = CENTRED;
    style.right = AUTO;
    style.bottom = AUTO;
    style.width = `${framebuffer.width}px`;
    style.height = `${framebuffer.height}px`;
    style.transform = `${CENTRING} ${QUARTER_TURN_CLOCKWISE} scale(${scale})`;
    this.framebuffer = framebuffer;
    if (canvas !== this.canvas) {
      this.restoreCanvas();
      this.canvas = canvas;
      Object.defineProperty(canvas, 'getBoundingClientRect', {
        configurable: true,
        value: () => this.frame(),
      });
      this.stopObservingCanvas = this.deps.observeResize(canvas, () =>
        this.layout(),
      );
    }
  }

  private straighten(): void {
    TURN_STYLE_PROPERTIES.forEach((property) =>
      this.screen.style.removeProperty(property),
    );
    this.restoreCanvas();
    this.pointer = null;
  }

  private restoreCanvas(): void {
    this.stopObservingCanvas?.();
    this.stopObservingCanvas = null;
    if (this.canvas) {
      Reflect.deleteProperty(this.canvas, 'getBoundingClientRect');
      this.canvas = null;
    }
  }

  /**
   * The box noVNC measures the canvas by while it handles an event: placed so that the position it
   * reads, minus the box's corner, is the framebuffer pixel that event was mapped to.
   */
  private frame(): DOMRect {
    const pointer = this.pointer ?? NO_POINTER;
    return rectOf(
      pointer.clientX - pointer.x,
      pointer.clientY - pointer.y,
      this.framebuffer,
    );
  }

  private mapPointer(event: MouseEvent): void {
    const point = this.framebufferPoint(
      event.target,
      event.clientX,
      event.clientY,
    );
    if (point) {
      this.pointer = {
        clientX: event.clientX,
        clientY: event.clientY,
        ...point,
      };
    }
  }

  private mapGesture(event: CustomEvent<GesturePoint>): void {
    const detail = event.detail;
    const point = this.framebufferPoint(
      event.target,
      detail.clientX,
      detail.clientY,
    );
    if (point) {
      detail.clientX = GESTURE_FRAME_ORIGIN + point.x;
      detail.clientY = GESTURE_FRAME_ORIGIN + point.y;
      this.pointer = {
        clientX: detail.clientX,
        clientY: detail.clientY,
        ...point,
      };
    }
  }

  /**
   * The framebuffer pixel under a page position on the turned canvas, or null when the screen is
   * not turned, the event is not the canvas's, or the canvas takes no room on the page.
   */
  private framebufferPoint(
    target: EventTarget | null,
    clientX: number,
    clientY: number,
  ): FramebufferPoint | null {
    const canvas = this.canvas;
    if (!canvas || target !== canvas) {
      return null;
    }
    // The canvas's own box on the page, not the frame that stands in for it.
    const box = Element.prototype.getBoundingClientRect.call(canvas);
    if (box.width <= 0 || box.height <= 0) {
      return null;
    }
    return framebufferPointOf(
      { left: clientX - box.left, top: clientY - box.top },
      box,
      this.framebuffer,
    );
  }
}
